import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { MfaChallengeService } from '../mfa/mfa-challenge.service';
import {
  MFA_TOKEN_REQUIRED,
  X_MFA_TOKEN,
  mfaErrorCode,
  resolveMfaAction
} from '../mfa/mfa.types';

/**
 * Reactive MFA gate. When a protected endpoint returns 403 MFA_TOKEN_REQUIRED,
 * open the OTP modal, obtain a single-use token, and retry the original request
 * with the `X-Mfa-Token` header. Mirrors the auth interceptor's 401→retry shape.
 *
 * Registered AFTER `authInterceptor` so the retried request still carries the
 * Bearer + X-Active-Context headers attached upstream. Any 403 that isn't
 * MFA_TOKEN_REQUIRED — or any request that isn't a known gated operation —
 * bubbles untouched to the call site (subscription gate, phone verification…).
 */
export const mfaInterceptor: HttpInterceptorFn = (req, next) => {
  const coordinator = inject(MfaChallengeService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 403) {
        return throwError(() => err);
      }
      if (mfaErrorCode(err) !== MFA_TOKEN_REQUIRED) {
        return throwError(() => err);
      }
      const action = resolveMfaAction(req.method, req.url);
      if (!action) {
        return throwError(() => err);
      }
      return coordinator.requestToken(action).pipe(
        switchMap(token => next(req.clone({ setHeaders: { [X_MFA_TOKEN]: token } })))
      );
    })
  );
};
